import {
  Plan,
  ProductType,
  Session,
  useCountriesByKeys,
  useCountryByPriceGroups,
  useRecruitment,
} from '@app/api';
import { Theme } from '@app/theme';
import { computeIncentive, NO_PRICE_GROUP } from 'lib/priceComputation';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { PriceEditionCountryCell } from './PriceEditionCountryCell';
import { getGroupProducts } from 'lib/planAndOffer';
import { useMemo } from 'react';

interface Props {
  plan: Plan;
  session: Session;
  sessionLinkId?: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  table: {
    ...theme.fonts.caption,
    maxWidth: theme.maxContentWidth.largeReduced,
    textAlign: 'left',
    backgroundColor: theme.colors.grayBackground,

    borderCollapse: 'collapse',
    borderRadius: theme.borderRadius.std,

    boxShadow: [0, 0, 0, 1, theme.colors.grayBorder],

    '& th': {
      ...theme.fonts.label,
    },
    '& tr': {
      borderBottom: {
        style: 'solid',
        color: theme.colors.grayBorder,
        width: 1,
      },
    },
    '& tr:last-child': {
      borderBottomWidth: 0,
    },
  },
  tableWhite: {
    backgroundColor: theme.colors.white,
  },
  cell: {
    padding: theme.marginBase,
  },
  priceCell: {
    textAlign: 'left',
    padding: 0,
  },
}));

export const PriceEditionCountriesTable = ({
  plan,
  session,
  sessionLinkId,
}: Props) => {
  const classes = useStyles();
  const { data: recruitment } = useRecruitment(session.recruitmentId);
  const countryKeys = recruitment?.countryKeys;

  const countryByKey = useCountriesByKeys();
  const countryByPriceGroups = useCountryByPriceGroups(countryKeys);

  const sortedGroups = useMemo(() => {
    const groups = Object.keys(countryByPriceGroups);
    if (!groups.length) {
      return [
        {
          groupId: NO_PRICE_GROUP,
          incentive: computeIncentive(plan.products, session.duration),
        },
      ];
    }
    return groups
      .map((groupId) => {
        return {
          groupId,
          incentive: computeIncentive(
            getGroupProducts(plan.products, groupId),
            session.duration,
          ),
        };
      })
      .sort((o1, o2) => o2.incentive - o1.incentive);
  }, [countryByPriceGroups, session.duration, plan.products]);

  return (
    <table
      className={classNames(classes.table, {
        [classes.tableWhite]: !!sessionLinkId,
      })}
    >
      <thead>
        <tr>
          <th className={classes.cell}>Pays</th>
          <th className={classes.cell}>Frais</th>
          <th className={classes.cell}>Contrepartie</th>
        </tr>
      </thead>
      <tbody>
        {sortedGroups.map(({ groupId, incentive }) => (
          <tr>
            <td className={classes.cell}>
              {groupId === NO_PRICE_GROUP
                ? "N'importe quel pays"
                : countryByPriceGroups[groupId]
                    .map((country) => countryByKey[country].text)
                    .join(', ')}
            </td>
            <td className={classes.priceCell}>
              <PriceEditionCountryCell
                plan={plan}
                duration={session.duration}
                sessionLinkId={sessionLinkId}
                groupId={groupId}
                type={ProductType.PARTICIPANT_FEE}
                incentive={incentive}
              />
            </td>
            <td className={classes.priceCell}>
              <PriceEditionCountryCell
                plan={plan}
                duration={session.duration}
                sessionLinkId={sessionLinkId}
                groupId={groupId}
                type={ProductType.INCENTIVE}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
